import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  InputAdornment,
  Stack,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import { SendWhatsAppMessageDialog } from "src-next/modules/crm-clinical/messaging";
import {
  mapFhirPatientToMessagingPatient,
  useConsultationWhatsAppSend,
} from "@/pages/adminInsights/utils/mapConsultationToWhatsApp";
import { READABLE_BODY_SX } from "src-next/modules/clinical-insights/constants/clinicalInsightsTheme";
import { useConsentNotAttendedPatients } from "src-next/modules/clinical-insights/hooks/useConsentNotAttendedPatients";
import { InsightPatientsBatchBar } from "src-next/modules/clinical-insights/components/workspace/patients/InsightPatientsBatchBar";
import { useFitRowCount } from "src-next/modules/clinical-insights/components/workspace/shell/useFitRowCount";
import { ConsentNotAttendedTable } from "./ConsentNotAttendedTable";

const TABLE_ROW_HEIGHT = 52;
const EMPTY_TEXT = "Nenhum paciente com consentimento sem atendimento.";
const LOADING_TEXT = "Carregando pacientes…";

interface ConsentNotAttendedPanelProps {
  onPatientStatsChange?: (filtered: number, total: number) => void;
}

export function ConsentNotAttendedPanel({
  onPatientStatsChange,
}: ConsentNotAttendedPanelProps) {
  const { rows, loading } = useConsentNotAttendedPatients();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const tableAreaRef = useRef<HTMLDivElement>(null);
  const rowsPerPage = useFitRowCount(tableAreaRef, TABLE_ROW_HEIGHT, 4);

  const { openSendBatch, isTemplatesLoading, hasTemplates, sendDialogProps } =
    useConsultationWhatsAppSend();

  const whatsAppDisabled = isTemplatesLoading || !hasTemplates;
  const whatsAppTooltip = isTemplatesLoading
    ? "Carregando modelos..."
    : hasTemplates
      ? "Enviar WhatsApp"
      : "Nenhum modelo de mensagem disponível";

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((row) => row.name.toLowerCase().includes(query));
  }, [rows, search]);

  const pageRows = useMemo(
    () => filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredRows, page, rowsPerPage],
  );

  const pageIds = pageRows.map((row) => row.patientId);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const somePageSelected = pageIds.some((id) => selectedIds.has(id));

  const selectedPatients = useMemo(
    () =>
      rows
        .filter((row) => selectedIds.has(row.patientId))
        .map((row) => mapFhirPatientToMessagingPatient(row.patient))
        .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry)),
    [rows, selectedIds],
  );

  useEffect(() => {
    if (loading) {
      onPatientStatsChange?.(0, 0);
      return;
    }
    onPatientStatsChange?.(filteredRows.length, rows.length);
  }, [loading, filteredRows.length, rows.length, onPatientStatsChange]);

  const from = filteredRows.length === 0 ? 0 : page * rowsPerPage + 1;
  const to = Math.min(filteredRows.length, (page + 1) * rowsPerPage);

  const togglePage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const toggleOne = (patientId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(patientId)) next.delete(patientId);
      else next.add(patientId);
      return next;
    });
  };

  return (
    <Stack spacing={2} sx={{ width: "100%", flex: 1, minHeight: 0, height: "100%" }}>
      {loading ? null : (
        <TextField
          size="small"
          placeholder="Buscar paciente"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(0);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      )}

      <Box ref={tableAreaRef} sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        {loading ? (
          <Typography color="text.secondary" sx={READABLE_BODY_SX}>
            {LOADING_TEXT}
          </Typography>
        ) : filteredRows.length === 0 ? (
          <Typography color="text.secondary" sx={READABLE_BODY_SX}>
            {search.trim() ? "Nenhum paciente encontrado." : EMPTY_TEXT}
          </Typography>
        ) : (
          <ConsentNotAttendedTable
            pageRows={pageRows}
            selectedIds={selectedIds}
            allPageSelected={allPageSelected}
            somePageSelected={somePageSelected}
            whatsAppDisabled={whatsAppDisabled}
            whatsAppTooltip={whatsAppTooltip}
            onTogglePage={togglePage}
            onToggleOne={toggleOne}
            onWhatsApp={(patient) => openSendBatch({ patients: [patient] })}
          />
        )}
      </Box>

      {loading ? null : (
        <>
          <InsightPatientsBatchBar
            selectedCount={selectedIds.size}
            whatsAppTooltip={whatsAppTooltip}
            whatsAppDisabled={whatsAppDisabled}
            canSendBatch={selectedPatients.length > 0}
            onSendBatch={() => openSendBatch({ patients: selectedPatients })}
            onClearSelection={() => setSelectedIds(new Set())}
          />

          <TablePagination
            component="div"
            count={filteredRows.length}
            page={page}
            onPageChange={(_event, nextPage) => setPage(nextPage)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[]}
            labelRowsPerPage=""
            labelDisplayedRows={() => `${from}–${to} de ${filteredRows.length}`}
            sx={{
              flexShrink: 0,
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-select": {
                display: "none",
              },
            }}
          />
        </>
      )}

      <SendWhatsAppMessageDialog {...sendDialogProps} />
    </Stack>
  );
}
